import { z } from "zod";
import { parentClaimRelationSchema, parentClaimSourceSchema } from "../enums.ts";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";
import { bookingFeeKindSchema } from "./fees.ts";

/**
 * The owner's action queue (mobile V2, slice 1.4; spec `teamna/strategy/mobile-v2-and-white-label.md`).
 * `GET /business/businesses/:id/queue`: what waits for the owner, in the order to do it, each with
 * the action Teamna suggests first (`primary`) and the other actions (`others`).
 *
 * The server decides what is open and which action comes first, with the same rules as Desk. Each
 * action names a route that exists already; the queue adds no new write. The phone shows the item
 * and calls the route of the action the owner taps. Desk Home moves onto this list later.
 *
 *   session_extra       a swimmer came and was not booked: take 1 session, or let it go
 *   parent_claim        a parent (find page) or a coach (Parent wanted) says whose names these are
 *   suggested_parent    a coach-added name, and a parent with the mobile number the coach wrote
 *   pending_member      joined with the code, not on the list: approve or decline
 *   member_participant  a parent added a child: confirm the group the parent picked
 *   names_waiting       the other coach-added names that wait for a parent: link them on Desk
 *   plan_next_month     the last planned session is 5 days away or less, or past: plan the next month on Desk
 *   cover_request       a staff member said yes to a cover, and the owner approves each cover
 *   fee_to_confirm      a no-show or late-cancel fee waits: send it with a card link, or waive it
 */

/** Days of the week, 0 is Sunday. */
const weekdaysSchema = z.array(z.number().int().min(0).max(6)).max(7);

export const ownerQueueActionSchema = z.discriminatedUnion("kind", [
  /** `POST .../session-extras/:extraId/decide {money: "take"}`. The phone may add `addWeekday`. */
  z.object({ kind: z.literal("take_session"), extraId: uuidSchema }),
  /** `POST .../session-extras/:extraId/decide {money: "free"}`. */
  z.object({ kind: z.literal("let_go"), extraId: uuidSchema }),
  /** `POST .../new-participants/claims/:claimId/link {weekdays}`. */
  z.object({ kind: z.literal("link_claim"), claimId: uuidSchema, weekdays: z.record(z.string(), weekdaysSchema) }),
  /** `POST .../new-participants/claims/:claimId/reject`. */
  z.object({ kind: z.literal("reject_claim"), claimId: uuidSchema }),
  /** `POST .../new-participants/:pendingId/link {to: {kind: "family", familyId}, weekdays}`. */
  z.object({ kind: z.literal("link_family"), pendingId: uuidSchema, familyId: uuidSchema, weekdays: weekdaysSchema }),
  /** `POST .../pending-members/:membershipId/approve` or `/decline`. */
  z.object({ kind: z.literal("approve_member"), membershipId: uuidSchema }),
  z.object({ kind: z.literal("decline_member"), membershipId: uuidSchema }),
  /** `PATCH .../participants/:participantId {confirm: true}`: the group the parent picked stays. */
  z.object({ kind: z.literal("confirm_participant"), participantId: uuidSchema }),
  /** `POST .../covers/:coverId/decide {decision: "approve"}` or `"refuse"` (migration 0072). */
  z.object({ kind: z.literal("approve_cover"), coverId: uuidSchema }),
  z.object({ kind: z.literal("refuse_cover"), coverId: uuidSchema }),
  /** `POST /owner/businesses/:id/fees/:feeId/send` or `/waive` (migration 0078). */
  z.object({ kind: z.literal("send_fee"), feeId: uuidSchema }),
  z.object({ kind: z.literal("waive_fee"), feeId: uuidSchema }),
  /** A screen, not a write. `desk`: the Desk page at `path`. `app`: the phone screen at `path`. */
  z.object({ kind: z.literal("open"), on: z.enum(["desk", "app"]), path: z.string() }),
]);
export type OwnerQueueAction = z.infer<typeof ownerQueueActionSchema>;
export type OwnerQueueActionKind = OwnerQueueAction["kind"];

const itemBase = {
  /** Unique in the list: `<kind>:<id>`. */
  key: z.string(),
  /** When it came in. */
  at: isoDateTimeSchema,
  primary: ownerQueueActionSchema,
  others: z.array(ownerQueueActionSchema),
};

export const ownerQueueItemSchema = z.discriminatedUnion("kind", [
  z.object({
    ...itemBase,
    kind: z.literal("session_extra"),
    extraId: uuidSchema,
    name: z.string(),
    groupName: z.string(),
    startsAt: isoDateTimeSchema,
    /** The coach who marked the swimmer. */
    staffName: z.string().nullable(),
    familyName: z.string().nullable(),
    /** The name has no parent yet: link one first. */
    waiting: z.boolean(),
    /** The family has a package with a session left for this level. */
    canTake: z.boolean(),
    /** The visit drops off at this time when nobody decides it. */
    dropsAt: isoDateTimeSchema,
    /** The session's weekday (0 is Sunday), and whether the swimmer has that day already. */
    weekday: z.number().int().min(0).max(6),
    onWeekday: z.boolean(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal("parent_claim"),
    claimId: uuidSchema,
    /** The names of the answer that still wait; with `secondAdult`, all the names of the answer. */
    names: z.array(z.string()),
    /** Every name of the answer has a parent now: Link makes the person a second adult of that family. */
    secondAdult: z.boolean(),
    /** The name the person typed, else the WhatsApp name. */
    parentName: z.string(),
    phoneE164: z.string(),
    /** The WhatsApp name, when it is not the name the person typed. */
    whatsappName: z.string().nullable(),
    relation: parentClaimRelationSchema.nullable(),
    source: parentClaimSourceSchema,
    coachName: z.string().nullable(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal("suggested_parent"),
    pendingId: uuidSchema,
    name: z.string(),
    groupName: z.string(),
    addedByName: z.string().nullable(),
    familyId: uuidSchema,
    parentName: z.string(),
    parentPhoneE164: z.string().nullable(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal("pending_member"),
    membershipId: uuidSchema,
    name: z.string(),
    phoneMasked: z.string(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal("member_participant"),
    participantId: uuidSchema,
    name: z.string(),
    /** The group the parent picked. Null: no group yet. */
    groupName: z.string().nullable(),
    familyName: z.string().nullable(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal("names_waiting"),
    /** Names that wait and are not in an item above. */
    count: z.number().int().min(1),
    coachNames: z.array(z.string()),
  }),
  z.object({
    ...itemBase,
    kind: z.literal("cover_request"),
    coverId: uuidSchema,
    /** The session's name, else its class. */
    groupName: z.string(),
    startsAt: isoDateTimeSchema,
    /** Who asked for cover, and who said yes. */
    askerName: z.string().nullable(),
    takerName: z.string().nullable(),
  }),
  z.object({
    ...itemBase,
    kind: z.literal("fee_to_confirm"),
    feeId: uuidSchema,
    feeKind: bookingFeeKindSchema,
    amountFils: z.number().int(),
    participantName: z.string(),
    /** The session's name, else its class. */
    groupName: z.string(),
    startsAt: isoDateTimeSchema,
  }),
  z.object({
    ...itemBase,
    kind: z.literal("plan_next_month"),
    /** The month to plan, `yyyy-MM`. `at` is the last planned session. */
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  }),
]);
export type OwnerQueueItem = z.infer<typeof ownerQueueItemSchema>;
export type OwnerQueueItemKind = OwnerQueueItem["kind"];

/** `GET /business/businesses/:id/queue`. */
export const ownerQueueResponseSchema = z.object({ items: z.array(ownerQueueItemSchema) });
export type OwnerQueueResponse = z.infer<typeof ownerQueueResponseSchema>;
